// ============================================================================
// ExpertiseMultiSelect.jsx — Multi-select tag selector for expertise values
// ============================================================================
// Renders selected items as chips/tags with removal.
// Dropdown with checkboxes, scrollable, with "+ Add row" at bottom.
// ============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, X, Plus, Loader2, Check } from 'lucide-react';

const FONT = "'Raleway', sans-serif";
const BRIO_GOLD = '#9f902a';
const BRIO_GOLD_LIGHT = 'rgba(159, 144, 42, 0.1)';
const BRIO_BORDER = '#e5e5e5';

export default function ExpertiseMultiSelect({
  value = [],
  onChange,
  options = [],
  onAddOption,
  disabled = false,
  canAddOptions = false,
  placeholder = 'Select expertise...',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const newInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setAddingNew(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  // Focus new input when adding
  useEffect(() => {
    if (addingNew && newInputRef.current) newInputRef.current.focus();
  }, [addingNew]);

  const selectedSet = new Set(value);

  const filteredOptions = options.filter(opt => {
    const label = typeof opt === 'string' ? opt : opt.label;
    return label.toLowerCase().includes(search.toLowerCase());
  });

  // Check if typed search text has an exact match in options
  const searchHasExactMatch = search.trim() && options.some(opt => {
    const label = typeof opt === 'string' ? opt : opt.label;
    return label.toLowerCase() === search.trim().toLowerCase();
  });

  const toggleOption = useCallback((label) => {
    if (disabled) return;
    const next = selectedSet.has(label)
      ? value.filter(v => v !== label)
      : [...value, label];
    onChange(next);
  }, [value, onChange, selectedSet, disabled]);

  const removeTag = useCallback((label, e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter(v => v !== label));
  }, [value, onChange, disabled]);

  const handleAddNew = async () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    setAddError('');
    setAddLoading(true);
    const result = await onAddOption(trimmed);
    setAddLoading(false);
    if (result.success) {
      setNewLabel('');
      setAddingNew(false);
      // Also select the new option
      if (!selectedSet.has(trimmed)) {
        onChange([...value, trimmed]);
      }
    } else {
      setAddError(result.error || 'Failed to add');
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', fontFamily: FONT }}>
      {/* Selected tags + trigger */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          padding: '5px 8px',
          border: `1px solid ${BRIO_BORDER}`,
          borderRadius: 0,
          minHeight: '34px',
          cursor: disabled ? 'default' : 'pointer',
          background: disabled ? '#f9f9f7' : 'white',
          alignItems: 'center',
        }}
      >
        {value.length === 0 && (
          <span style={{ color: '#9ca3af', fontSize: '13px' }}>{placeholder}</span>
        )}
        {value.map(tag => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '1px 6px',
              background: BRIO_GOLD_LIGHT,
              color: BRIO_GOLD,
              fontSize: '11px',
              fontWeight: 500,
              borderRadius: '2px',
              lineHeight: '18px',
            }}
          >
            {tag}
            {!disabled && (
              <X
                size={10}
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onClick={(e) => removeTag(tag, e)}
              />
            )}
          </span>
        ))}
        <ChevronDown
          size={14}
          style={{
            marginLeft: 'auto',
            color: '#9ca3af',
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            background: 'white',
            border: `1px solid ${BRIO_BORDER}`,
            borderTop: 'none',
            maxHeight: '260px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {/* Search / type to add */}
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${BRIO_BORDER}` }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setAddingNew(false); setAddError(''); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && search.trim() && !searchHasExactMatch && canAddOptions) {
                  e.preventDefault();
                  // Directly add the typed value
                  const trimmed = search.trim();
                  setAddLoading(true);
                  onAddOption(trimmed).then(result => {
                    setAddLoading(false);
                    if (result.success) {
                      if (!selectedSet.has(trimmed)) onChange([...value, trimmed]);
                      setSearch('');
                    } else {
                      setAddError(result.error || 'Failed to add');
                    }
                  });
                }
              }}
              placeholder="Type to search or add..."
              style={{
                width: '100%',
                padding: '4px 6px',
                border: `1px solid ${BRIO_BORDER}`,
                borderRadius: 0,
                fontSize: '12px',
                fontFamily: FONT,
                outline: 'none',
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOptions.map(opt => {
              const label = typeof opt === 'string' ? opt : opt.label;
              const checked = selectedSet.has(label);
              return (
                <div
                  key={label}
                  onClick={() => toggleOption(label)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontFamily: FONT,
                    background: checked ? BRIO_GOLD_LIGHT : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f5f5f3'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = checked ? BRIO_GOLD_LIGHT : 'transparent'; }}
                >
                  <div
                    style={{
                      width: '14px',
                      height: '14px',
                      border: `1.5px solid ${checked ? BRIO_GOLD : '#cbd5e1'}`,
                      borderRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: checked ? BRIO_GOLD : 'transparent',
                      flexShrink: 0,
                    }}
                  >
                    {checked && <Check size={10} color="white" strokeWidth={3} />}
                  </div>
                  <span style={{ color: checked ? BRIO_GOLD : '#374151' }}>{label}</span>
                </div>
              );
            })}

            {filteredOptions.length === 0 && !search.trim() && (
              <div style={{ padding: '8px 10px', fontSize: '12px', color: '#9ca3af', fontFamily: FONT }}>
                No matching options
              </div>
            )}
          </div>

          {/* Add from search — shown when typing has no exact match */}
          {canAddOptions && search.trim() && !searchHasExactMatch && (
            <div style={{ borderTop: `1px solid ${BRIO_BORDER}`, padding: '4px 8px' }}>
              <div
                onClick={() => {
                  const trimmed = search.trim();
                  setAddLoading(true);
                  onAddOption(trimmed).then(result => {
                    setAddLoading(false);
                    if (result.success) {
                      if (!selectedSet.has(trimmed)) onChange([...value, trimmed]);
                      setSearch('');
                    } else {
                      setAddError(result.error || 'Failed to add');
                    }
                  });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '5px 4px', cursor: addLoading ? 'wait' : 'pointer',
                  fontSize: '12px', fontWeight: 500, color: BRIO_GOLD, fontFamily: FONT,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = BRIO_GOLD_LIGHT; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {addLoading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                <span>Add "{search.trim()}"</span>
              </div>
              {addError && (
                <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px', fontFamily: FONT }}>{addError}</div>
              )}
            </div>
          )}

          {/* + Add row — shown when not searching */}
          {canAddOptions && !search.trim() && (
            <div style={{ borderTop: `1px solid ${BRIO_BORDER}`, padding: '4px 8px' }}>
              {!addingNew ? (
                <div
                  onClick={() => { setAddingNew(true); setAddError(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '5px 4px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: 500, color: BRIO_GOLD, fontFamily: FONT,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = BRIO_GOLD_LIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <Plus size={13} />
                  <span>Add row</span>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input
                      ref={newInputRef}
                      type="text"
                      value={newLabel}
                      onChange={e => { setNewLabel(e.target.value); setAddError(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') handleAddNew(); if (e.key === 'Escape') { setAddingNew(false); setNewLabel(''); } }}
                      placeholder="New expertise..."
                      style={{ flex: 1, padding: '4px 6px', border: `1px solid ${BRIO_BORDER}`, borderRadius: 0, fontSize: '12px', fontFamily: FONT, outline: 'none' }}
                    />
                    <button
                      onClick={handleAddNew}
                      disabled={addLoading || !newLabel.trim()}
                      style={{ padding: '4px 10px', background: BRIO_GOLD, color: 'white', border: 'none', borderRadius: 0, fontSize: '11px', fontWeight: 600, cursor: addLoading ? 'wait' : 'pointer', fontFamily: FONT, opacity: addLoading || !newLabel.trim() ? 0.5 : 1 }}
                    >
                      {addLoading ? <Loader2 size={12} className="animate-spin" /> : 'Add'}
                    </button>
                    <button
                      onClick={() => { setAddingNew(false); setNewLabel(''); setAddError(''); }}
                      style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {addError && (
                    <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px', fontFamily: FONT }}>{addError}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
